import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import useClientChat from '../client-chat/useClientChat';
import { useChatContext } from '../../context/ChatContext';

const GlobalChatWidget = ({ teamId: teamIdProp, userId: userIdProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { activeContext, authContext } = useChatContext();
  const location = useLocation();

  const teamId = teamIdProp || authContext?.teamId;
  const userId = userIdProp || authContext?.userId;
  const clientId = activeContext?.client_id || null;
  const projectId = activeContext?.project_id || null;

  const displayName = useMemo(() => {
    if (activeContext?.view === 'project' && projectId) return 'this project';
    if (activeContext?.view === 'client' && clientId) return 'this client';
    return 'Athena';
  }, [activeContext?.view, projectId, clientId]);

  const {
    messages,
    chatInput,
    setChatInput,
    isSending,
    startNewChat,
    handleSubmit
  } = useClientChat({
    isOpen,
    teamId,
    userId,
    clientId,
    projectId,
    clientName: displayName,
    activeView: activeContext?.view
  });

  const canSend = Boolean(teamId && userId);
  const sendDisabled = isSending || !chatInput.trim() || !canSend;
  const messagesEndRef = useRef(null);
  const isPortalRoute = location.pathname.startsWith('/portal') || location.pathname.startsWith('/client');

  useEffect(() => {
    if (isOpen && !canSend) {
      console.warn('Chat disabled: missing required identifiers', { teamId, userId, sendDisabled });
    }
  }, [isOpen, canSend, teamId, userId, sendDisabled]);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isSending, isOpen]);

  if (!canSend || isPortalRoute) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      >
        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
          <img
            src="https://storage.googleapis.com/msgsndr/MKPEENbCxfwSh3Priffu/media/689b8f36f580c4518569934a.png"
            alt="Athena Logo"
            className="w-8 h-8 object-contain"
          />
        </div>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center p-4">
      <div className="bg-white rounded-t-2xl w-full max-w-md flex flex-col animate-slide-up shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-pink-600 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <img
                  src="https://storage.googleapis.com/msgsndr/MKPEENbCxfwSh3Priffu/media/689b8f36f580c4518569934a.png"
                  alt="Athena Logo"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <div>
                <h3 className="font-semibold text-white">Athena Chat</h3>
                <p className="text-purple-100 text-sm">Feature coming soon</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-purple-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Chatting with Athena is coming soon</h3>
          <p className="text-gray-600 text-sm mb-5">
            We are still finishing this assistant experience. For now, this widget is a placeholder and chat messages are disabled.
          </p>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalChatWidget;
